import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, SmokeAlarm, FireExtinguisher } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

function deviceAge(year: number | null): number | null {
  if (!year) return null;
  return CURRENT_YEAR - year;
}

function isExpiredAlarm(alarm: SmokeAlarm): boolean {
  return (deviceAge(alarm.install_year) ?? 0) >= 10;
}

function isOverdueTest(alarm: SmokeAlarm): boolean {
  if (!alarm.last_tested) return true;
  const diff = (Date.now() - new Date(alarm.last_tested).getTime()) / 86400000;
  return diff > 30;
}

function isExtPastLife(ext: FireExtinguisher): boolean {
  return (deviceAge(ext.purchase_year) ?? 0) >= 12;
}

function safetyScore(
  alarms: SmokeAlarm[],
  exts: FireExtinguisher[],
): { score: number; label: string; color: string } {
  if (alarms.length === 0 && exts.length === 0) {
    return { score: 0, label: 'No data', color: '#9ca3af' };
  }
  let deductions = 0;
  deductions += alarms.filter(isExpiredAlarm).length * 30;
  deductions += alarms.filter(isOverdueTest).length * 10;
  deductions += exts.filter(isExtPastLife).length * 20;
  if (alarms.length === 0) deductions += 40;
  if (exts.length === 0)   deductions += 20;
  const score = Math.max(0, 100 - deductions);
  const label = score >= 90 ? 'Excellent' : score >= 70 ? 'Good' : score >= 50 ? 'Fair' : 'Needs attention';
  const color = score >= 90 ? '#15803d' : score >= 70 ? '#1D4ED8' : score >= 50 ? '#b45309' : '#b91c1c';
  return { score, label, color };
}

export function SafetyHub() {
  const navigate = useNavigate();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];

  const { data: alarmsData } = useQuery({
    queryKey: ['smoke-alarms', home?.id],
    queryFn: () => api.get<{ data: SmokeAlarm[] }>(`/homes/${home!.id}/smoke-alarms`),
    enabled: !!home,
  });

  const { data: extsData } = useQuery({
    queryKey: ['fire-extinguishers', home?.id],
    queryFn: () => api.get<{ data: FireExtinguisher[] }>(`/homes/${home!.id}/fire-extinguishers`),
    enabled: !!home,
  });

  const alarms = alarmsData?.data ?? [];
  const exts   = extsData?.data ?? [];

  const expiredAlarms   = alarms.filter(isExpiredAlarm).length;
  const overdueTests    = alarms.filter(isOverdueTest).length;
  const overdueExts     = exts.filter(isExtPastLife).length;
  const hasShutters     = home?.has_storm_shutters ?? false;
  const hurricaneRisk   = home?.hurricane_risk;

  const { score, label, color } = safetyScore(alarms, exts);

  function NavCard({ icon, title, subtitle, to, badgeCount, badgeColor }: {
    icon: string;
    title: string;
    subtitle: string;
    to: string;
    badgeCount?: number;
    badgeColor?: string;
  }) {
    return (
      <button
        onClick={() => navigate(to)}
        style={{
          background: '#fff', borderRadius: 12, padding: '16px',
          border: '1.5px solid #e5e7eb', display: 'flex', alignItems: 'center',
          gap: 14, width: '100%', textAlign: 'left', cursor: 'pointer',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: '#f3f4f6', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 22, flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#111827' }}>{title}</p>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {badgeCount !== undefined && badgeCount > 0 && (
            <span style={{
              background: badgeColor ?? '#ef4444', color: '#fff',
              borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 700,
            }}>
              {badgeCount}
            </span>
          )}
          <span style={{ color: '#9ca3af', fontSize: 18 }}>›</span>
        </div>
      </button>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Home Safety</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
          Fire, storm, and household safety tracking
        </p>
      </div>

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Safety score */}
        <div style={{ background: '#fff', borderRadius: 16, padding: '20px', border: '1.5px solid #e5e7eb' }}>
          <p style={{ margin: '0 0 4px', fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Safety score</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              border: `4px solid ${color}`, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 20, fontWeight: 700, color }}>{score}</span>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 17, color }}>{label}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                {alarms.length} alarm{alarms.length !== 1 ? 's' : ''} · {exts.length} extinguisher{exts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {(expiredAlarms > 0 || overdueTests > 0 || overdueExts > 0) && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#fef2f2', borderRadius: 8 }}>
              {expiredAlarms > 0 && (
                <p style={{ margin: 0, fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>
                  ⚠ {expiredAlarms} smoke alarm{expiredAlarms > 1 ? 's' : ''} expired (10-year lifespan)
                </p>
              )}
              {overdueTests > 0 && (
                <p style={{ margin: expiredAlarms > 0 ? '4px 0 0' : 0, fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>
                  ⚠ {overdueTests} alarm{overdueTests > 1 ? 's' : ''} not tested in 30+ days
                </p>
              )}
              {overdueExts > 0 && (
                <p style={{ margin: (expiredAlarms > 0 || overdueTests > 0) ? '4px 0 0' : 0, fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>
                  ⚠ {overdueExts} extinguisher{overdueExts > 1 ? 's' : ''} past 12-year replacement date
                </p>
              )}
            </div>
          )}
        </div>

        {/* Nav cards */}
        <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Safety systems
        </p>

        <NavCard
          icon="🔥"
          title="Smoke Alarms"
          subtitle={alarms.length > 0 ? `${alarms.length} alarm${alarms.length !== 1 ? 's' : ''} — ${expiredAlarms > 0 ? `${expiredAlarms} expired` : 'all current'}` : 'No alarms added yet'}
          to="/app/smoke-alarms"
          badgeCount={expiredAlarms + overdueTests}
          badgeColor="#ef4444"
        />

        <NavCard
          icon="🧯"
          title="Fire Extinguishers"
          subtitle={exts.length > 0 ? `${exts.length} extinguisher${exts.length !== 1 ? 's' : ''} tracked` : 'No extinguishers added yet'}
          to="/app/fire-extinguishers"
          badgeCount={overdueExts}
          badgeColor="#ef4444"
        />

        <NavCard
          icon="🏠"
          title="Roof Profile"
          subtitle={home?.roof_age_years != null ? `Roof is ~${home.roof_age_years} years old` : 'Track roof age and inspection dates'}
          to="/app/roof"
        />

        <NavCard
          icon="🗑️"
          title="Trash & Recycling"
          subtitle={home?.trash_day ? `Trash: ${home.trash_day.charAt(0).toUpperCase() + home.trash_day.slice(1)}${home.recycling_day ? ` · Recycling: ${home.recycling_day.charAt(0).toUpperCase() + home.recycling_day.slice(1)}` : ''}` : 'Set your pickup schedule'}
          to="/app/trash-schedule"
        />

        {(hurricaneRisk === 'moderate' || hurricaneRisk === 'high' || hasShutters) && (
          <>
            <p style={{ margin: '8px 0 0', fontSize: 13, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Storm protection
            </p>
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#111827' }}>Storm Shutters</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                {hasShutters
                  ? `${home?.shutter_type ? home.shutter_type.replace('_', ' ') : 'Shutters'} installed${home?.impact_windows ? ' · Impact windows' : ''}`
                  : 'No shutters recorded'}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9ca3af' }}>
                Update storm protection details in Home Profile → Edit
              </p>
            </div>
          </>
        )}
      </div>

      <NavBar />
    </div>
  );
}
